import { useMemo } from 'react';

import { useQuery } from '@apollo/client';

import logger from 'js/app/loggerSingleton';
import { isAuthenticatedUser } from 'js/lib/user';

import { useNaptime } from 'bundles/naptimejs';
import SubscriptionsV1 from 'bundles/naptimejs/resources/subscriptions.v1';
import useIsSubscriptionsGatewayMigrationEnabled from 'bundles/subscriptions-common/hooks/useIsSubscriptionsGatewayMigrationEnabled';
import Subscriptions from 'bundles/subscriptions-common/models/Subscriptions';
import QueryMySubscriptions from 'bundles/subscriptions-common/queries/QueryMySubscriptions.graphql';
import type {
  QueryMySubscriptionsQuery,
  QueryMySubscriptionsQueryVariables,
} from 'bundles/subscriptions-common/queries/__generated__/QueryMySubscriptions';

type NaptimeSubscriptionsData = {
  subscriptions: Array<SubscriptionsV1>;
};

export type PropsFromUseGetLoggedInUserSubscriptions = NaptimeSubscriptionsData & {
  pending: boolean;
};

type InputProps = {
  skip?: boolean;
};

export const useGetLoggedInUserSubscriptions = ({ skip }: InputProps) => {
  const isGatewayMigrationEnabled = useIsSubscriptionsGatewayMigrationEnabled();
  const shouldSkip = skip || !isAuthenticatedUser();

  const { data: gatewayData, loading: gatewayPending } = useQuery<
    QueryMySubscriptionsQuery,
    QueryMySubscriptionsQueryVariables
  >(QueryMySubscriptions, {
    context: { clientName: 'gatewayGql' },
    skip: shouldSkip || !isGatewayMigrationEnabled,
    onError: (error) => logger.error(`Failed to fetch user subscriptions from gateway: ${error.message}`),
  });

  const {
    data: { subscriptions: naptimeSubscriptions },
    pending: naptimePending,
  } = useNaptime<NaptimeSubscriptionsData>(() => {
    if (shouldSkip || isGatewayMigrationEnabled) {
      return {};
    }

    return {
      subscriptions: SubscriptionsV1.me({
        fields: [
          'cartId',
          'id',
          'isActive',
          'nextBillingAt',
          'paymentProcessorId',
          'productItemId',
          'productType',
          'status',
          'productSku',
          'paymentInformation',
          'subscriptionId',
        ],
      }),
    };
  }, [isGatewayMigrationEnabled, shouldSkip]);

  const gatewaySubscriptions = useMemo(
    () =>
      gatewayData?.SubscriptionsQueries?.queryMySubscriptions.map((subscription) => new Subscriptions(subscription)),
    [gatewayData?.SubscriptionsQueries?.queryMySubscriptions]
  );

  if (isGatewayMigrationEnabled) {
    return {
      /**  Adapted to the naptime public shape so consumers work unchanged during the migration.
           Known gaps, neither exercised by current consumers:
          `paymentInformation` / `stripeSubscriptionPaymentInformation`: no gateway equivalent.
          `nextBillingAt`: `number | undefined` here vs. required on naptime.
           paymentProcessorId`: `string` here vs. the narrower shared `PaymentProcessorType` on naptime. */
      subscriptions: (gatewaySubscriptions ?? []) as unknown as Array<SubscriptionsV1>,
      pending: gatewayPending,
    };
  }

  return { subscriptions: naptimeSubscriptions ?? [], pending: naptimePending };
};
